import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Icon from '../../components/common/Icon';
import { AREA_COLOURS, AREA_OTHER_COLOUR, type TourAreaGroup, type TourStation } from '../../utils/tourAreas';
import { assignScans, newArea, pointInPolygon, removeScans } from '../../utils/tourAreaEdit';
import styles from './TourAreaEditor.module.css';

// Drawing the Virtual Tour's areas by hand (Advanced Virtual Tour, "Areas
// drawn by hand"): a plan of every scan, north up. Drag around scans to pick
// them, then make them a new area or add them to one; scans in no area are
// split by the gaps between scans.

interface Props {
  stations: TourStation[];
  // Storey of each scan (0 = lowest) and how many there are.
  levelOf: Map<string, number>;
  levels: number;
  groups: TourAreaGroup[];
  onSave: (groups: TourAreaGroup[]) => Promise<void>;
  onClose: () => void;
}

interface View { cx: number; cy: number; scale: number }

const FALLBACK = { w: 760, h: 520 };

const TourAreaEditor = ({ stations, levelOf, levels, groups: saved, onSave, onClose }: Props) => {
  const [groups, setGroups] = useState<TourAreaGroup[]>(() => saved.map((g) => ({ ...g, stationIds: [...g.stationIds] })));
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [floor, setFloor] = useState<number | 'all'>('all');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lasso, setLasso] = useState<Array<[number, number]> | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [size, setSize] = useState(FALLBACK);
  const gesture = useRef<{ kind: 'lasso' | 'pan'; add: boolean; x: number; y: number; length: number } | null>(null);

  useEffect(() => {
    const el = svgRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      const r = el.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) setSize({ w: r.width, h: r.height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const onFloor = useCallback((s: TourStation) => floor === 'all' || levelOf.get(s.id) === floor, [floor, levelOf]);
  const fit = useCallback((): View => {
    const shown = stations.filter(onFloor);
    const list = shown.length ? shown : stations;
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    for (const s of list) {
      minX = Math.min(minX, s.position.x); maxX = Math.max(maxX, s.position.x);
      minY = Math.min(minY, s.position.y); maxY = Math.max(maxY, s.position.y);
    }
    if (!Number.isFinite(minX)) return { cx: 0, cy: 0, scale: 1 };
    const scale = Math.min((size.w - 80) / Math.max(maxX - minX, 4), (size.h - 80) / Math.max(maxY - minY, 4));
    return { cx: (minX + maxX) / 2, cy: (minY + maxY) / 2, scale: Math.min(Math.max(scale, 0.05), 80) };
  }, [stations, onFloor, size]);
  const [view, setView] = useState<View>(fit);
  const fitKey = `${floor}|${size.w}x${size.h}|${stations.length}`;
  useEffect(() => { setView(fit()); }, [fitKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const toScreen = useCallback((x: number, y: number) => ({
    x: size.w / 2 + (x - view.cx) * view.scale,
    y: size.h / 2 - (y - view.cy) * view.scale,
  }), [size, view]);

  const groupOf = useMemo(() => {
    const m = new Map<string, number>();
    groups.forEach((g, gi) => g.stationIds.forEach((id) => m.set(id, gi)));
    return m;
  }, [groups]);
  const colourOf = (gi: number) => AREA_COLOURS[gi] ?? AREA_OTHER_COLOUR;
  const free = stations.filter((s) => !groupOf.has(s.id)).length;
  const pickedList = useMemo(() => stations.filter((s) => picked.has(s.id)).map((s) => s.id), [stations, picked]);

  // Wheel zooms about the cursor (a native listener: React's is passive).
  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const r = el.getBoundingClientRect();
      const mx = e.clientX - r.left, my = e.clientY - r.top;
      setView((v) => {
        const scale = Math.min(Math.max(v.scale * Math.exp(-e.deltaY * 0.0015), 0.05), 200);
        const wx = v.cx + (mx - size.w / 2) / v.scale, wy = v.cy - (my - size.h / 2) / v.scale;
        return { scale, cx: wx - (mx - size.w / 2) / scale, cy: wy + (my - size.h / 2) / scale };
      });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [size]);

  const local = (e: React.PointerEvent) => {
    const r = svgRef.current!.getBoundingClientRect();
    return [e.clientX - r.left, e.clientY - r.top] as [number, number];
  };
  const onPointerDown = (e: React.PointerEvent) => {
    const [x, y] = local(e);
    (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
    if (e.button === 0) {
      gesture.current = { kind: 'lasso', add: e.shiftKey, x, y, length: 0 };
      setLasso([[x, y]]);
    } else {
      gesture.current = { kind: 'pan', add: false, x, y, length: 0 };
    }
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const g = gesture.current;
    if (!g) return;
    const [x, y] = local(e);
    const d = Math.hypot(x - g.x, y - g.y);
    if (g.kind === 'pan') {
      setView((v) => ({ ...v, cx: v.cx - (x - g.x) / v.scale, cy: v.cy + (y - g.y) / v.scale }));
      g.x = x; g.y = y;
      return;
    }
    if (d < 3) return;
    g.length += d; g.x = x; g.y = y;
    setLasso((pts) => (pts ? [...pts, [x, y]] : [[x, y]]));
  };
  const onPointerUp = () => {
    const g = gesture.current;
    gesture.current = null;
    if (g?.kind === 'lasso' && lasso && g.length > 12 && lasso.length > 2) {
      const inside = stations.filter((s) => {
        if (!onFloor(s)) return false;
        const p = toScreen(s.position.x, s.position.y);
        return pointInPolygon(p.x, p.y, lasso);
      }).map((s) => s.id);
      setPicked((prev) => new Set(g.add ? [...prev, ...inside] : inside));
    }
    setLasso(null);
  };

  const toggle = (id: string) => setPicked((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const makeArea = () => {
    const { groups: next } = newArea(groups, pickedList);
    setGroups(next);
    setPicked(new Set());
  };
  const addTo = (id: string) => {
    setGroups(assignScans(groups, pickedList, id));
    setPicked(new Set());
  };
  const takeOut = () => {
    setGroups(removeScans(groups, pickedList));
    setPicked(new Set());
  };

  const named = groups.every((g) => g.stationIds.length === 0 || g.name.trim());
  const save = async () => {
    if (!named || saving) return;
    setSaving(true);
    setError(null);
    try {
      await onSave(groups.filter((g) => g.stationIds.length > 0).map((g) => ({ ...g, name: g.name.trim() })));
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The areas could not be saved');
    } finally {
      setSaving(false);
    }
  };

  const labels = groups.map((g, gi) => {
    const own = stations.filter((s) => groupOf.get(s.id) === gi && onFloor(s));
    if (!own.length) return null;
    const x = own.reduce((a, s) => a + s.position.x, 0) / own.length;
    const y = Math.max(...own.map((s) => s.position.y));
    const at = toScreen(x, y);
    return { id: g.id, name: g.name || 'Unnamed', at, colour: colourOf(gi) };
  });

  return (
    <div className={styles.backdrop} onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); setPicked(new Set()); } }}>
      <section className={styles.panel} role="dialog" aria-label="Draw tour areas">
        <header className={styles.head}>
          <span className={styles.title}>Draw tour areas</span>
          {levels > 1 && (
            <label className={styles.floor}>
              <span>Floor</span>
              <select value={floor} onChange={(e) => setFloor(e.target.value === 'all' ? 'all' : Number(e.target.value))}>
                <option value="all">All floors</option>
                {Array.from({ length: levels }, (_, i) => <option key={i} value={i}>Floor {i + 1}</option>)}
              </select>
            </label>
          )}
          <button type="button" className={styles.iconBtn} onClick={() => setView(fit())} aria-label="Fit the plan">
            <Icon name="fit_screen" />
          </button>
          <button type="button" className={styles.iconBtn} onClick={onClose} aria-label="Close without saving">
            <Icon name="close" />
          </button>
        </header>
        <div className={styles.body}>
          <div className={styles.planWrap}>
            <svg
              ref={svgRef}
              className={styles.plan}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerUp}
              onContextMenu={(e) => e.preventDefault()}
            >
              {stations.map((s) => {
                const p = toScreen(s.position.x, s.position.y);
                const gi = groupOf.get(s.id);
                const isPicked = picked.has(s.id);
                const here = onFloor(s);
                return (
                  <circle
                    key={s.id}
                    role="checkbox"
                    aria-checked={isPicked}
                    aria-label={`Scan ${s.index + 1}`}
                    cx={p.x}
                    cy={p.y}
                    r={isPicked ? 6.5 : 5}
                    className={here ? styles.dot : styles.dotOther}
                    fill={gi === undefined ? '#ffffff' : colourOf(gi)}
                    stroke={isPicked ? '#0061ff' : gi === undefined ? '#7b849a' : '#ffffff'}
                    strokeWidth={isPicked ? 3 : 1.5}
                    onClick={() => { if (here) toggle(s.id); }}
                  />
                );
              })}
              {labels.map((l) => l && (
                <text key={l.id} x={l.at.x} y={l.at.y - 12} className={styles.label} fill={l.colour}>{l.name}</text>
              ))}
              {lasso && lasso.length > 1 && (
                <polygon points={lasso.map(([x, y]) => `${x},${y}`).join(' ')} className={styles.lasso} />
              )}
            </svg>
            <p className={styles.hint}>Drag around scans to pick them; Shift adds to the pick. Right-drag moves the plan, the wheel zooms.</p>
          </div>
          <aside className={styles.side}>
            <div className={styles.block}>
              <h3 className={styles.blockTitle}>{pickedList.length ? `${pickedList.length} ${pickedList.length === 1 ? 'scan' : 'scans'} picked` : 'No scans picked'}</h3>
              <div className={styles.actions}>
                <button type="button" className={styles.primary} disabled={!pickedList.length} onClick={makeArea}>New area</button>
                <select
                  className={styles.addTo}
                  aria-label="Add the picked scans to an area"
                  disabled={!pickedList.length || !groups.length}
                  value=""
                  onChange={(e) => { if (e.target.value) addTo(e.target.value); }}
                >
                  <option value="">Add to…</option>
                  {groups.map((g) => <option key={g.id} value={g.id}>{g.name || 'Unnamed'}</option>)}
                </select>
                <button type="button" className={styles.secondary} disabled={!pickedList.length} onClick={takeOut}>Take out of areas</button>
                <button type="button" className={styles.link} disabled={!pickedList.length} onClick={() => setPicked(new Set())}>Clear</button>
              </div>
            </div>
            <div className={`${styles.block} ${styles.areas}`}>
              <h3 className={styles.blockTitle}>Areas</h3>
              {groups.length === 0 && <p className={styles.empty}>Pick scans on the plan, then New area.</p>}
              {groups.map((g, gi) => (
                <div key={g.id} className={styles.row}>
                  <button
                    type="button"
                    className={styles.swatch}
                    style={{ background: colourOf(gi) }}
                    aria-label={`Pick the scans of ${g.name || 'this area'}`}
                    onClick={() => setPicked(new Set(g.stationIds))}
                  />
                  <input
                    className={styles.name}
                    value={g.name}
                    maxLength={60}
                    aria-label={`Name of area ${gi + 1}`}
                    onChange={(e) => setGroups(groups.map((x) => (x.id === g.id ? { ...x, name: e.target.value } : x)))}
                  />
                  <span className={styles.count}>{g.stationIds.length}</span>
                  <button
                    type="button"
                    className={styles.iconBtnSmall}
                    aria-label={`Delete ${g.name || 'this area'}`}
                    onClick={() => setGroups(groups.filter((x) => x.id !== g.id))}
                  >
                    <Icon name="delete" />
                  </button>
                </div>
              ))}
              <p className={styles.note}>
                {free === 0 ? 'Every scan is in an area.' : `${free} ${free === 1 ? 'scan is' : 'scans are'} in no area: they are split by the gaps between scans.`}
              </p>
            </div>
          </aside>
        </div>
        <footer className={styles.foot}>
          {error && <span className={styles.error} role="alert">{error}</span>}
          {!named && <span className={styles.error}>Every area needs a name.</span>}
          <button type="button" className={styles.secondary} onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className={styles.primary} onClick={() => void save()} disabled={saving || !named}>
            {saving ? 'Saving…' : 'Save areas'}
          </button>
        </footer>
      </section>
    </div>
  );
};

export default TourAreaEditor;
